import React from 'react';
import posthog from 'posthog-js';
import App from './src/containers/App';
import { onWindowLoadEvent, performDeviceIdentification, setPostHogInitialized } from './src/components/Analytics';
import { initGlobalBehaviorSignal } from './src/components/BehaviorSignalProvider';
import { installClarityProxy, initClarity } from './src/components/clarityProxy';
import { startOpenReplay } from './src/components/openReplay';
import { PageErrorBoundary } from './src/components/ErrorBoundary';
import { SITE_ROOT, IS_DEVELOPMENT, IS_ALPHA_LIKE, POSTHOG_ENABLED, CLARITY_PROJECT_ID } from './src/api-config.mjs';
import { captureOppref } from './src/utils/chatgptAdsAttribution';
import { installMemoryStorageIfBlocked } from './src/utils/blockedStorage';
import { sendReportKeptOverReload } from './src/utils/reportError';
import log from 'loglevel';
import "@fontsource/sarabun/latin-400.css";


// Both wrappers are error boundaries (src/components/ErrorBoundary.js), mirrored in
// gatsby-ssr.js. The page one catches almost everything and resets when the route
// changes; the outer one catches what App itself throws, which nothing inside can.
export const wrapRootElement = (props) => (
  <PageErrorBoundary name="root">
    <App {...props} browser>{props.element}</App>
  </PageErrorBoundary>
);

export const wrapPageElement = ({ element, props }) => (
  <PageErrorBoundary name="page" resetKey={props.location.pathname}>
    {element}
  </PageErrorBoundary>
);

export const onClientEntry = () => {
  log.debug("onClientEntry");

  // First, before anything reads storage: where the browser blocks this site's storage,
  // swap in one that lasts for this page, so the many unguarded reads in the app work
  // instead of crashing its first render (src/utils/blockedStorage.js).
  installMemoryStorageIfBlocked();

  // Keep the ChatGPT Ads click id from the landing URL. Here and not in a page or
  // layout: this runs once per full page load, on every route, before any
  // client-side redirect can drop the query string.
  captureOppref(window.location.search);

  // PostHog product analytics — proxied through first-party domain to bypass ad blockers.
  // Docs: https://posthog.com/docs/libraries/js
  // Proxy docs: https://posthog.com/docs/advanced/proxy
  if (POSTHOG_ENABLED) {
    // A developer stage reports to the ALPHA project, like everything else that is not
    // per-account (IS_ALPHA_LIKE in api-config.mjs). It used to pick by IS_ALPHA_STAGE,
    // which sent developer stages' page views into the prod project (measured 2026-10-06:
    // touhidul., mansoor. and asher.earningscall.biz pageviews in prod) -- and meant a
    // flag or experiment had to be live in PROD before a developer stage could be
    // bucketed by it.
    //
    // The developer opt-in (ENABLE_POSTHOG_IN_DEVELOPMENT) always reports to alpha,
    // whatever stage the dev server was started with: `GATSBY_CFN_STAGE=prod yarn
    // develop` is how a prod-only issue is reproduced, and localhost traffic must
    // never land in the prod project.
    const posthogApiKey = IS_ALPHA_LIKE || IS_DEVELOPMENT
      ? 'phc_94at20iKfqs2wcx3x9VDyqtmHADD1YvzxGnIxAE8qND'  // Alpha project
      : 'phc_WgVSA3kzH5x5U9U22VT4OhpYZgv0LjHMDtm2M95G6FD'; // Prod project
    posthog.init(posthogApiKey, {
      // Reverse proxy via CloudFront (see cca repo backend.ts). localhost has none, so the
      // developer opt-in talks to PostHog's ingest host directly.
      api_host: IS_DEVELOPMENT ? 'https://us.i.posthog.com' : `${SITE_ROOT}/pd`,
      ui_host: 'https://us.posthog.com',  // Required for toolbar/surveys when using a proxy
      person_profiles: 'identified_only', // Only create person profiles for identified users
    });
    setPostHogInitialized();
  }

  // Microsoft Clarity — proxied through first-party domain to bypass ad
  // blockers. installClarityProxy() must run before initClarity() so the
  // runtime's telemetry is rewritten. Skipped entirely until a per-stage
  // CLARITY_PROJECT_ID is configured (so we don't patch globals for nothing).
  // Never under `yarn develop`: the PostHog opt-in above must not drag Clarity
  // along, since with GATSBY_CFN_STAGE=prod the project id would be prod's.
  if (!IS_DEVELOPMENT && CLARITY_PROJECT_ID) {
    installClarityProxy();
    initClarity(CLARITY_PROJECT_ID);
  }

  // OpenReplay session recording, started here rather than from a component so that it
  // is already running when the first render throws, and survives React removing the
  // page. Bot and stage checks are inside, and it never throws.
  startOpenReplay();
  // An error caught just before the previous page reloaded itself, which that page could
  // not hand over in time (src/utils/reportError.js).
  sendReportKeptOverReload();

  performDeviceIdentification();
  window.addEventListener("load", onWindowLoadEvent);

  // Initialize global behavior signal collector
  // All API requests will automatically include the behavior signal header
  initGlobalBehaviorSignal({
    enabled: true,  // Set to false to disable
    debug: false,   // Set to true for development debugging
  });
};

export const onInitialClientRender = () => {
  log.debug("ReactDOM.render has executed");
};

export const disableCorePrefetching = () => {
  log.debug("disableCorePrefetching() -> return true");
  return true;
};
